// lib/assistant-events.ts
import React from 'react';

/**
 * Defines the structure for assistant actions.
 */
export interface AssistantAction {
  action: string; // e.g., 'create_article'
  message: string; // a user facing message
  payload: any;   // Data associated with the action
  messageId?: string; // Optional message ID for augmenting the UI message
}

export const ASSISTANT_ACTION_EVENT = 'assistant-action';
export const ASSISTANT_ACTION_RESULT_EVENT = 'assistant-action-result';

/**
 * Dispatches a custom event for an assistant action.
 * @param action The assistant action to dispatch.
 */
export function dispatchAssistantAction(action: AssistantAction) {
  const event = new CustomEvent(ASSISTANT_ACTION_EVENT, { detail: action });
  window.dispatchEvent(event);
  console.log('Dispatched assistant action:', action);
}

/**
 * Dispatches the result of an action back to the UI.
 * @param messageId The ID of the message to augment
 * @param result The action result (e.g., link info)
 */
export function dispatchAssistantActionResult(messageId: string, result: any) {
  const event = new CustomEvent(ASSISTANT_ACTION_RESULT_EVENT, { 
    detail: { messageId, result } 
  });
  window.dispatchEvent(event);
  console.log('Dispatched action result for message:', messageId, result);
}

/**
 * A React hook for listening to assistant actions.
 * @param onAction A callback function to handle the assistant action.
 */
export function useAssistantActionListener(onAction: (action: AssistantAction) => void) {
  React.useEffect(() => {
    const handleEvent = (event: Event) => {
      const customEvent = event as CustomEvent<AssistantAction>;
      if (customEvent.detail && customEvent.detail.action) {
        onAction(customEvent.detail);
      }
    };

    window.addEventListener(ASSISTANT_ACTION_EVENT, handleEvent);

    return () => {
      window.removeEventListener(ASSISTANT_ACTION_EVENT, handleEvent);
    };
  }, [onAction]);
}
